import { builtIns } from "../commands";
import { useLantern } from "../hooks";
import { navigate } from "../router";
import { t } from "../i18n";
import { modKey, shortcutGroups } from "./Shortcuts";
import { Card, Page, Tabs, useHashTab } from "./ui";

// HelpView is Lantern's help, at /help: getting started, search, formatting
// and shortcuts for everyone, and the admin pages for admins. Tabs are kept
// in the address (/help#search), so a tab can be linked to.

type Tab = "start" | "search" | "formatting" | "shortcuts" | "admin";

// Pairs is a list of what to type, or press, and what it does.
function Pairs({ rows, keys }: { rows: [string, string][]; keys?: boolean }) {
  return (
    <dl className="help-pairs">
      {rows.map(([what, does]) => (
        <div key={what}>
          <dt>{keys ? <kbd>{what}</kbd> : <code>{what}</code>}</dt>
          <dd>{does}</dd>
        </div>
      ))}
    </dl>
  );
}

function Points({ items }: { items: string[] }) {
  return (
    <ul className="help-points">
      {items.map((it) => (
        <li key={it}>{it}</li>
      ))}
    </ul>
  );
}

export function HelpView() {
  const state = useLantern();
  const admin = state.me?.role === "admin";
  const tabs: [Tab, string][] = [
    ["start", t("Getting started")],
    ["search", t("Search")],
    ["formatting", t("Formatting")],
    ["shortcuts", t("Keyboard shortcuts")],
    ...(admin ? ([["admin", t("For admins")]] as [Tab, string][]) : []),
  ];
  const [tab, setTab] = useHashTab(tabs.map(([k]) => k));

  return (
    <Page title={t("Help")} description={t("How to find your way around Lantern, search it, write in it and get more done from the keyboard.")}>
      <Tabs tabs={tabs} active={tab} onChange={setTab} label={t("Help")} />

      {tab === "start" && (
        <>
          <Card title={t("Channels and conversations")}>
            <Points
              items={[
                t("Jump to any channel, person or page with {key}, or from the box at the top, which also searches messages.", { key: `${modKey}K` }),
                t("Browse or create channels with the + beside Channels. Star a channel to keep it on top, or file it in a section of your own from its ⋯ menu."),
                t("Write to people from New message, beside the workspace's name. A direct message's details can start a new one with more people in it."),
                t("Your inbox collects mentions, your keywords, direct messages, reactions to your messages, reminders and tasks waiting for you. Threads lists the threads you follow; Unreads, what you have not read."),
              ]}
            />
          </Card>
          <Card title={t("Messages")}>
            <Points
              items={[
                t("Point at a message to react, reply in a thread or save it for later. More actions edits, deletes, pins, shares, copies a link, marks it unread or sets a reminder; soon after sending, it also recalls the message, which everyone then sees was recalled."),
                t("A reply stays in its thread; tick Also send under the box to show it in the channel too."),
                t("What you type and do not send stays in its channel as a draft. The clock beside Send schedules a message; Drafts & sent lists your drafts, and Scheduled the messages waiting to go."),
              ]}
            />
          </Card>
          <Card title={t("Commands")} description={t("Type / in the box for these and the commands of the workspace's apps.")}>
            <Pairs rows={builtIns().map((c) => [c.usage_hint ? `${c.command} ${c.usage_hint}` : c.command, c.description])} />
          </Card>
          <Card title={t("You and your settings")}>
            <Points
              items={[
                t("Set a status, go away or pause notifications from your picture at the foot of the sidebar."),
                t("Settings has your language, theme, text size and spacing; sound, pop-ups, desktop notifications, keywords and quiet hours; and the agents that may act for you."),
                t("Start a huddle from a channel's header, or from Huddles in the sidebar, to talk, with video and your screen if you like, where your workspace has calls."),
                t("Lantern installs as a desktop app from the workspace menu, where your browser allows it."),
              ]}
            />
          </Card>
        </>
      )}

      {tab === "search" && (
        <>
          <Card title={t("Filters")} description={t("Put them anywhere among the words; they combine. The search page has them as buttons too.")}>
            <Pairs
              rows={[
                ["from:@handle", t("Messages from someone")],
                ["in:#channel", t("Messages in a channel")],
                ["before:2026-10-21", t("Before a day; after: is after one. today and yesterday work as days too.")],
                ["on:2026-10-21", t("On a day")],
                ["during:2026-10", t("During a month, or a year: during:2026")],
                ["has:file", t("With a file")],
                ["has:link", t("With a link")],
                ["is:thread", t("Replies in threads")],
                ["is:saved", t("Messages you saved for later")],
              ]}
            />
          </Card>
          <Card title={t("Words")}>
            <Pairs
              rows={[
                ['"release notes"', t("These words together, in this order")],
                ["-draft", t("Not this word")],
                ["deploy OR release", t("Either word")],
              ]}
            />
            <p className="help-note">{t("Ask, on the search page, answers a question from the messages you can read and cites them, where your workspace has a model.")}</p>
          </Card>
        </>
      )}

      {tab === "formatting" && (
        <Card title={t("Formatting")} description={t("Type these in a message, or use the toolbar under the box.")}>
          <Pairs
            rows={[
              ["*bold*", t("Bold; **bold** works too")],
              ["_italic_", t("Italic")],
              ["~strike~", t("Struck through")],
              ["`code`", t("Code within a line")],
              ["```go", t("A code block, to the closing ```; a language after the opening one colors it")],
              ["> quote", t("A quote")],
              ["- item", t("A list; 1. numbers it")],
              ["[text](https://…)", t("A link with text of its own")],
              ["@handle", t("Mention someone, who is notified")],
              [":tada:", t("An emoji, by its name")],
            ]}
          />
        </Card>
      )}

      {tab === "shortcuts" && (
        <>
          {shortcutGroups().map(([title, list]) => (
            <Card key={title} title={title}>
              <Pairs rows={list} keys />
            </Card>
          ))}
        </>
      )}

      {tab === "admin" && admin && (
        <Card
          title={t("Running the workspace")}
          description={t("Invite people from the workspace menu. Installing, backing up, upgrading and licensing Lantern are in the operations guides that come with it.")}
        >
          <ul className="rows help-admin">
            {(
              [
                [t("Administration"), t("The workspace's name and defaults, invitations, how long messages are kept, and data-loss rules."), "/admin/workspace"],
                [t("People"), t("Everyone in the workspace, and groups people can mention at once."), "/people"],
                [t("Custom emoji"), t("Emoji of the workspace's own."), "/emoji"],
                [t("eDiscovery"), t("Find, hold and export messages for a legal matter."), "/admin/discovery"],
                [t("System"), t("Whether the server and what it depends on are well, its license, and a support bundle for the vendor."), "/admin/system"],
                [t("Models"), t("The language and embedding models features and agents use, and the servers that run them."), "/admin/models"],
                [t("Hosted agents"), t("Agents Lantern runs itself, and what each may use."), "/admin/hosted"],
                [t("Fleet"), t("Agents' queues and pools, and the tasks that need a person."), "/fleet"],
                [t("Workflows"), t("Automations started by a message, a reaction, a schedule, a command, a webhook, a task or a shortcut, or by hand."), "/workflows"],
              ] as const
            ).map(([name, what, path]) => (
              <li key={path}>
                <div className="row-main">
                  <span className="row-title">{name}</span>
                  <span className="row-meta">{what}</span>
                </div>
                <button onClick={() => navigate(path)}>{t("Open")}</button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </Page>
  );
}
